import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminCalendar } from "../../lib/api";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, ExternalLink,
} from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-yellow-100 text-yellow-800 border-yellow-200",
  in_review: "bg-purple-100 text-purple-800 border-purple-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function startOfWeek(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
}

function fmtDate(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}

export default function Calendar() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));

  useEffect(() => {
    setLoading(true);
    adminCalendar()
      .then((d) => setPosts(d.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [weekStart]);

  // Bucket posts: scheduled posts on their scheduled_at date; non-scheduled go in a side rail.
  const postsByDay = useMemo(() => {
    const map = days.map(() => []);
    for (const p of posts) {
      const dateSrc = p.status === "scheduled" ? p.scheduled_at : (p.status === "published" ? p.created_at : null);
      if (!dateSrc) continue;
      const pd = new Date(dateSrc);
      for (let i = 0; i < days.length; i++) {
        if (sameDay(pd, days[i])) { map[i].push(p); break; }
      }
    }
    return map;
  }, [posts, days]);

  const backlog = useMemo(
    () => posts.filter((p) => p.status === "draft" || p.status === "in_review"),
    [posts]
  );

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(startOfWeek(d));
  };

  const weekLabel = `${fmtDate(days[0])} – ${fmtDate(days[6])}, ${days[6].getFullYear()}`;
  const today = new Date();

  return (
    <div data-testid="calendar-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">Editorial Calendar</h1>
          <p className="text-charcoal-soft text-sm mt-1">
            Weekly grid of what's scheduled, what shipped, and what's still waiting in the backlog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftWeek(-1)} className="btn-ghost text-sm px-3 py-2" data-testid="calendar-prev-week">
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="btn-ghost text-sm px-3 py-2" data-testid="calendar-this-week">
            <CalendarIcon className="w-4 h-4" /> This week
          </button>
          <button onClick={() => shiftWeek(1)} className="btn-ghost text-sm px-3 py-2" data-testid="calendar-next-week">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="text-sm font-semibold text-charcoal-soft mb-3" data-testid="calendar-week-label">{weekLabel}</div>

      {loading ? (
        <div className="text-charcoal-soft py-10 text-center">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-4 gap-4">
          {/* Week grid takes 3 columns on desktop */}
          <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2 bg-white rounded-2xl border border-black/5 p-3">
            {days.map((d, i) => {
              const isToday = sameDay(d, today);
              const dayPosts = postsByDay[i];
              return (
                <div
                  key={i}
                  className={`min-h-[140px] p-2 rounded-lg border ${
                    isToday ? "border-forest bg-forest/5" : "border-black/5"
                  }`}
                  data-testid={`calendar-day-${i}`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-charcoal-soft">
                      {DAY_LABELS[i]}
                    </span>
                    <span className={`text-base font-display font-bold ${isToday ? "text-forest" : "text-charcoal"}`}>
                      {d.getDate()}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dayPosts.length === 0 ? (
                      <div className="text-[10px] text-charcoal-soft/60">—</div>
                    ) : (
                      dayPosts.map((p) => (
                        <Link
                          key={p.id}
                          to="/admin/blog"
                          className={`block p-1.5 rounded-md text-[11px] border leading-tight ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}
                          data-testid={`calendar-post-${p.id}`}
                          title={`${p.title} — ${p.status}`}
                        >
                          <div className="font-semibold line-clamp-2">{p.title}</div>
                          <div className="opacity-75 mt-0.5">{p.status === "scheduled" ? "Scheduled" : "Published"}</div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Backlog rail */}
          <div className="bg-white rounded-2xl border border-black/5 p-4" data-testid="calendar-backlog">
            <h2 className="font-display font-bold text-forest mb-3">Backlog</h2>
            <p className="text-xs text-charcoal-soft mb-3">
              Drafts and posts in review with no scheduled date.
            </p>
            {backlog.length === 0 ? (
              <div className="text-sm text-charcoal-soft">Nothing in the backlog — nice work.</div>
            ) : (
              <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {backlog.map((p) => (
                  <Link
                    key={p.id}
                    to="/admin/blog"
                    className={`block p-2 rounded-md text-xs border leading-tight ${STATUS_STYLES[p.status] || STATUS_STYLES.draft}`}
                    data-testid={`calendar-backlog-${p.id}`}
                  >
                    <div className="font-semibold line-clamp-2">{p.title}</div>
                    <div className="opacity-75 mt-0.5 flex items-center gap-1">
                      {p.status === "in_review" ? "In Review" : "Draft"}
                      <ExternalLink className="w-3 h-3 ml-auto" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
